<template>
  <div class="overflow-x-hidden">
    <div class="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="flex flex-wrap items-center gap-3">
        <div class="relative">
          <input v-model="searchQuery" placeholder="Buscar (nome ou ticker)..." class="form-input pl-10 w-64" />
        </div>
        <button @click="exportToCSV" class="btn-secondary !py-2 !px-3">
          <span class="material-symbols-outlined align-middle mr-1 text-base">download</span>
          CSV
        </button>
        <div class="flex items-center gap-2 flex-wrap">
          <span class="form-label-inline">Filtrar:</span>
          <button @click="variationFilter = 'all'"
            :class="variationFilter === 'all' ? 'btn-filter-active' : 'btn-filter-inactive'">Todos</button>
          <button @click="variationFilter = 'up'"
            :class="variationFilter === 'up' ? 'bg-green-600 text-white' : 'btn-filter-inactive'">Em Alta</button>
          <button @click="variationFilter = 'down'"
            :class="variationFilter === 'down' ? 'bg-red-600 text-white' : 'btn-filter-inactive'">Em Baixa</button>
          <button @click="showWatchlistOnly = !showWatchlistOnly"
            :class="showWatchlistOnly ? 'bg-amber-500 text-white' : 'btn-filter-inactive'">
            <span class="material-symbols-outlined align-middle mr-1 text-base">star</span> Favoritos
          </button>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-3 justify-start lg:justify-end">
        <div class="flex items-center gap-2">
          <label for="sort" class="form-label-inline">Ordenar por:</label>
          <select id="sort" v-model="sortBy" class="form-select !rounded-full">
            <option value="volume">Maior Volume</option>
            <option value="market_cap">Maior Valor de Mercado</option>
            <option value="highChange">Maiores Altas</option>
            <option value="lowChange">Maiores Baixas</option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <label for="sector" class="form-label-inline">Setor:</label>
          <select id="sector" v-model="sectorFilter" class="form-select !rounded-full">
            <option value="all">Todos</option>
            <option v-for="sector in allSectors" :key="sector" :value="sector">{{ sector }}</option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <label for="perpage" class="form-label-inline">Itens/página:</label>
          <select id="perpage" v-model.number="pageSize" class="form-select !rounded-full">
            <option :value="24">24</option>
            <option :value="48">48</option>
            <option :value="96">96</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="i in 9" :key="i" class="skeleton-card"></div>
    </div>

    <div v-else-if="error" class="error-state">
      <h3 class="mt-1 text-2xl font-semibold text-red-700 dark:text-red-300">Falha ao carregar dados</h3>
      <p class="mt-2 text-slate-600 dark:text-slate-400">{{ error }}</p>
      <button @click="manualRefresh" class="mt-4 btn-danger">Tentar novamente</button>
    </div>

    <div v-else>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-if="pagedAcoes.length === 0" class="col-span-full empty-state">
          <p class="mt-4 text-xl font-semibold">Nenhum ativo encontrado.</p>
        </div>

        <div v-for="acao in pagedAcoes" :key="acao.stock" class="card-b3 group">
          <div>
            <div class="flex items-start justify-between gap-4">
              <div class="flex items-center gap-4 flex-1 min-w-0">
                <img v-if="acao.logo" :src="acao.logo" :alt="acao.name" class="card-logo">
                <div v-else class="card-logo-fallback">
                  <span class="font-bold text-xl text-brand-600 dark:text-brand-500">{{ acao.stock.charAt(0) }}</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-bold text-xl text-slate-800 dark:text-slate-100 truncate" :title="acao.stock">{{
                    acao.stock }}</p>
                  <p class="text-sm text-slate-500 dark:text-slate-400 truncate" :title="acao.name">{{ acao.name }}</p>
                </div>
              </div>

              <div class="flex flex-col items-end gap-2">
                <button @click.stop="toggleWatchlist(acao.stock)" class="btn-fav"
                  :title="isFav(acao.stock) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'">
                  <span class="material-symbols-outlined"
                    :class="isFav(acao.stock) ? 'text-amber-500' : 'text-slate-400'">star</span>
                </button>
                <div class="px-2.5 py-1.5 rounded-full text-sm font-bold text-right"
                  :class="getChangeClass(acao.change)">
                  {{ acao.change >= 0 ? '▲' : '▼' }} {{ Math.abs(acao.change || 0).toFixed(2) }}%
                </div>
              </div>
            </div>

            <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/50 space-y-2 text-sm">
              <div class="flex justify-between items-center text-slate-600 dark:text-slate-300">
                <span>Volume</span>
                <span class="font-mono font-semibold text-slate-800 dark:text-slate-100">{{
                  formatBigNumber(acao.volume) }}</span>
              </div>
              <div class="flex justify-between items-center text-slate-600 dark:text-slate-300">
                <span>Valor de Mercado</span>
                <span class="font-mono font-semibold text-slate-800 dark:text-slate-100">{{
                  formatBigNumber(acao.market_cap) }}</span>
              </div>
              <div class="flex justify-between items-center text-slate-600 dark:text-slate-300 gap-2">
                <span>Setor</span>
                <span class="font-semibold text-slate-800 dark:text-slate-100 truncate text-right"
                  :title="acao.sector">{{ acao.sector || '--' }}</span>
              </div>
            </div>
          </div>

          <div class="mt-5 text-right">
            <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">Preço Atual</p>
            <p class="font-bold text-3xl text-slate-800 dark:text-slate-100">
              <span v-if="typeof acao.close === 'number' && acao.close > 0" class="text-xl align-middle">R$</span>
              {{ formatCurrency(acao.close) }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="totalPages > 1" class="mt-8 flex items-center justify-center gap-2">
        <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1"
          class="btn-secondary !px-3 !py-1.5">Anterior</button>
        <span class="text-sm text-slate-600 dark:text-slate-300">Página {{ currentPage }} de {{ totalPages }}</span>
        <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages"
          class="btn-secondary !px-3 !py-1.5">Próxima</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import axios from 'axios'

const allAcoes = ref([])
const isLoading = ref(true)
const error = ref(null)
const lastUpdate = ref('')
const searchQuery = ref('')
const variationFilter = ref('all')
const sortBy = ref('volume')
const sectorFilter = ref('all')
const showWatchlistOnly = ref(false)
const watchlist = ref(new Set(JSON.parse(localStorage.getItem('b3_watchlist_ids') || '[]')))
const autoRefresh = ref(true)
const refreshInterval = ref(5)
let timer = null
const CACHE_KEY = 'b3_list_cache'
const CACHE_TTL_MS = 15 * 60 * 1000
const pageSize = ref(24)
const currentPage = ref(1)

const allSectors = computed(() => {
  const sectors = allAcoes.value.map(a => a.sector).filter(Boolean)
  return [...new Set(sectors)].sort()
})

const _filteredBase = computed(() => {
  let acoes = [...allAcoes.value]
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase()
    acoes = acoes.filter(a => a.stock?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q))
  }
  if (variationFilter.value === 'up') {
    acoes = acoes.filter(a => (toNum(a.change) ?? 0) > 0)
  } else if (variationFilter.value === 'down') {
    acoes = acoes.filter(a => (toNum(a.change) ?? 0) < 0)
  }
  if (sectorFilter.value !== 'all') {
    acoes = acoes.filter(a => a.sector === sectorFilter.value)
  }
  if (showWatchlistOnly.value) {
    acoes = acoes.filter(a => watchlist.value.has(a.stock))
  }
  switch (sortBy.value) {
    case 'volume': acoes.sort((a, b) => (b.volume || 0) - (a.volume || 0)); break
    case 'market_cap': acoes.sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0)); break
    case 'ticker': acoes.sort((a, b) => (a.stock || '').localeCompare(b.stock || '')); break
    case 'highPrice': acoes.sort((a, b) => (b.close || 0) - (a.close || 0)); break
    case 'lowPrice': acoes.sort((a, b) => (a.close || 0) - (b.close || 0)); break
    case 'highChange': acoes.sort((a, b) => (toNum(b.change) ?? -Infinity) - (toNum(a.change) ?? -Infinity)); break
    case 'lowChange': acoes.sort((a, b) => (toNum(a.change) ?? Infinity) - (toNum(b.change) ?? Infinity)); break
  }
  return acoes
})

const totalPages = computed(() => Math.max(1, Math.ceil(_filteredBase.value.length / pageSize.value)))

const pagedAcoes = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return _filteredBase.value.slice(start, start + pageSize.value)
})

const toNum = (v) => typeof v === 'string' ? parseFloat(v) : v

const formatBigNumber = (num) => {
  if (num == null) return '--'
  const n = Math.abs(num)
  if (n >= 1e12) return `R$ ${(num / 1e12).toFixed(2)} T`
  if (n >= 1e9)  return `R$ ${(num / 1e9).toFixed(2)} B`
  if (n >= 1e6)  return `R$ ${(num / 1e6).toFixed(2)} M`
  if (n >= 1e3)  return `R$ ${(num / 1e3).toFixed(1)} mil`
  return `R$ ${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(num)}`
}

const formatCurrency = (value) => {
  if (typeof value !== 'number') return '--'
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getChangeClass = (change) => {
  const value = toNum(change)
  if (value == null) return 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
  if (value > 0) return 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300'
  if (value < 0) return 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300'
  return 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
}

const toggleWatchlist = (ticker) => {
  if (watchlist.value.has(ticker)) watchlist.value.delete(ticker)
  else watchlist.value.add(ticker)
  localStorage.setItem('b3_watchlist_ids', JSON.stringify(Array.from(watchlist.value)))
}
const isFav = (ticker) => watchlist.value.has(ticker)

const fetchAcoes = async () => {
  isLoading.value = true
  error.value = null
  const apiUrl = `https://brapi.dev/api/quote/list`

  const cached = localStorage.getItem(CACHE_KEY)
  const now = Date.now()
  if (cached) {
    try {
      const { timestamp, data } = JSON.parse(cached)
      if (now - timestamp < CACHE_TTL_MS) {
        allAcoes.value = postProcess(data)
        lastUpdate.value = new Date(timestamp).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        isLoading.value = false
        return
      }
    }
    // eslint-disable-next-line no-empty
    catch { }
  }

  try {
    const { data } = await axios.get(apiUrl, { params: { limit: 500 } })
    if (data && data.stocks) {
      allAcoes.value = postProcess(data.stocks)
      localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: now, data: data.stocks }))
      lastUpdate.value = new Date(now).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
      currentPage.value = 1
    } else {
      throw new Error('A resposta da API não continha os dados esperados.')
    }
  } catch (err) {
    console.error('Erro ao buscar dados da bolsa:', err)
    if (err?.response) {
      error.value = `Erro da API: ${err.response.status}. Tente novamente mais tarde.`
    } else if (err?.request) {
      error.value = 'Não foi possível se conectar à API. Verifique sua conexão.'
    } else {
      error.value = 'Ocorreu um erro inesperado ao processar os dados.'
    }
  } finally {
    isLoading.value = false
  }
}

const postProcess = (arr) => {
  return arr
    .filter(a =>
      a?.name && a?.close && a?.volume &&
      (a?.stock?.endsWith('3') || a?.stock?.endsWith('4') || a?.stock?.endsWith('11'))
    )
    .map(a => ({
      ...a,
      change: toNum(a.change),
    }))
}

const manualRefresh = async () => {
  localStorage.removeItem(CACHE_KEY)
  await fetchAcoes()
}

const goToPage = (p) => {
  currentPage.value = Math.min(Math.max(1, p), totalPages.value)
}

const exportToCSV = () => {
  const rows = _filteredBase.value
  if (!rows.length) return

  const headers = ['Ticker', 'Nome', 'Preço', 'Variação (%)', 'Volume', 'Valor de Mercado', 'Setor']
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const fmt = (n) => typeof n === 'number' ? n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n

  const csvRows = rows.map(a => [
    a.stock,
    a.name,
    fmt(a.close),
    (toNum(a.change) ?? 0).toFixed(2),
    a.volume,
    a.market_cap,
    a.sector || ''
  ].map(esc).join(','))

  const csv = '\uFEFF' + headers.join(',') + '\n' + csvRows.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `b3_radar_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
}

let searchT
watch(searchQuery, () => {
  clearTimeout(searchT)
  searchT = setTimeout(() => { currentPage.value = 1 }, 250)
})

watch([variationFilter, sortBy, sectorFilter, showWatchlistOnly, pageSize], () => {
  currentPage.value = 1
})

const setupTimer = () => {
  clearInterval(timer)
  if (autoRefresh.value) {
    timer = setInterval(fetchAcoes, refreshInterval.value * 60 * 1000)
  }
}
watch([autoRefresh, refreshInterval], setupTimer)

onMounted(async () => {
  await fetchAcoes()
  setupTimer()
})
onBeforeUnmount(() => clearInterval(timer))
</script>

<style scoped>
.form-input {
  @apply w-full px-3 py-2 text-sm bg-white dark:bg-brand-800/50 border border-solid border-slate-300 dark:border-slate-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200;
}
.form-label-inline {
  @apply text-sm font-semibold text-slate-600 dark:text-slate-400;
}
.form-select {
  @apply py-1.5 text-sm bg-white dark:bg-brand-800 border-slate-300 dark:border-slate-700 focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm transition-colors;
}
.btn-filter-inactive {
  @apply bg-white dark:bg-brand-800 text-slate-700 dark:text-slate-300;
}
.btn-filter-active {
  @apply bg-brand-600 text-white;
}
.btn-fav {
  @apply w-9 h-9 grid place-items-center rounded-full border border-slate-200 dark:border-slate-700 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition;
}
.btn-danger {
  @apply mt-4 px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white transition;
}
.btn-secondary {
  @apply flex items-center px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition disabled:opacity-50 text-slate-700 dark:text-slate-200;
}
.card-b3 {
  @apply bg-white dark:bg-brand-800 p-5 rounded-2xl shadow-md border-2 border-transparent flex flex-col justify-between transition-all duration-300;
}
.card-logo {
  @apply w-14 h-14 rounded-full bg-white dark:bg-slate-700 object-contain p-1 shadow-sm flex-shrink-0;
}
.card-logo-fallback {
  @apply w-14 h-14 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0;
}
.skeleton-card {
  @apply bg-white dark:bg-brand-800/50 p-5 rounded-2xl shadow-md animate-pulse h-64;
}
.empty-state {
  @apply col-span-full text-center py-16 bg-white dark:bg-brand-800 rounded-2xl text-slate-600 dark:text-slate-300;
}
.error-state {
  @apply text-center py-12 bg-red-50 dark:bg-red-900/20 rounded-2xl;
}
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
button[class*="btn-filter"] {
  @apply px-3 py-1.5 text-sm font-semibold rounded-full shadow-sm transition-colors flex items-center;
}
</style>